import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AdminTheme } from './admin-theme';

/**
 * Exported because it has a copy outside TypeScript's reach: the pre-paint script in `index.html`
 * inlines the literal, and nothing would fail if a rename left the two disagreeing — the script would
 * simply stop finding the preference and the scheme would flash on every load. `theme.service.spec.ts`
 * pins that copy against this constant.
 */
export const THEME_STORAGE_KEY = 'admin-theme';

/**
 * Owns the active colour scheme and is the only thing in the app that writes it to the document.
 *
 * RxJS-only and signal-free, like `CountryDomainService` — stores bridge `themeChanges$` to signals
 * via `rxMethod`. The preference is per browser rather than per staff account: it is a display
 * choice about the machine being used, and storing it locally means it survives a token refresh and
 * costs no round-trip on boot.
 *
 * Applying a scheme is two attribute writes on `<html>` and nothing else. Because `styles.scss`
 * builds the theme with `theme-type: color-scheme`, every `--mat-sys-*` colour is a
 * `light-dark(<light>, <dark>)` pair that the CSS `color-scheme` property selects between, so the
 * whole palette — Material, the `--na-*` overlays derived from it, and the component styles that
 * reference them — repaints without Angular re-rendering anything.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
    private readonly theme$ = new BehaviorSubject<AdminTheme>(readStoredTheme());

    /**
     * The scheme is applied from the constructor rather than an initializer so it lands during
     * bootstrap, before the shell first paints — the service is instantiated by `AppShellStore`, which
     * the root component provides and injects. A stored *dark* preference is a no-op against the stylesheet default,
     * so only staff who chose light depend on this running early.
     */
    constructor() {
        this.applyToDocument(this.theme$.value);
    }

    readonly themeChanges$: Observable<AdminTheme> = this.theme$.asObservable();

    get activeTheme(): AdminTheme {
        return this.theme$.value;
    }

    /** Switches to the scheme the toolbar did not just show, persisting the result. */
    toggle(): void {
        this.setTheme(this.theme$.value === AdminTheme.Dark ? AdminTheme.Light : AdminTheme.Dark);
    }

    setTheme(theme: AdminTheme): void {
        if (theme === this.theme$.value) {
            return;
        }

        this.applyToDocument(theme);

        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch {
            // Best-effort, matching the app's other stored preferences: blocked storage still gets a
            // working switch, it just reverts to the default on the next load.
        }

        this.theme$.next(theme);
    }

    private applyToDocument(theme: AdminTheme): void {
        const root = document.documentElement;

        root.style.colorScheme = theme;
        // AG Grid resolves its own light/dark parameters from this attribute and does NOT read the
        // `color-scheme` property, so the grids need it set explicitly or they stay dark on a light
        // page. See `grid-defaults.ts` in `@natkor/ng-shared/grid`, which selects the mode-aware colour scheme part.
        root.setAttribute('data-ag-theme-mode', theme);
    }
}

/**
 * The stored preference, defaulting to dark — the app's long-standing appearance, and what staff who
 * never open the switch keep seeing. Unreadable or unrecognised values fall back rather than throw,
 * so a blocked `localStorage` or a hand-edited key cannot stop the app from booting.
 */
function readStoredTheme(): AdminTheme {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);

        return stored === AdminTheme.Light ? AdminTheme.Light : AdminTheme.Dark;
    } catch {
        return AdminTheme.Dark;
    }
}
